// Production plans — the kitchen's demand sheet (kitchen flow, Stage 2A).
// The chef lists what will be cooked for a date, sees what it needs, locks
// the plan, sends the store one request for it and plans the prep cooking.
// Strings: lib/inventoryFlow/planStrings.ts. Rules: lib/inventoryFlow/planLogic.ts.
import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { AlertTriangle, ArrowLeft, ChevronDown, Plus, Printer, Trash2 } from 'lucide-react'
import apiClient from '@/api/client'
import { friendlyError } from '@/lib/inventoryFlow/friendlyError'
import { formatMoney } from '@/lib/currency'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { getStoredUserRole } from '@/lib/staff-roles'
import { cn, formatDateHuman } from '@/lib/utils'
import { newIdempotencyKey } from '@/lib/inventoryFlow/offlineQueue'
import { intentScope, useIntentKey, useIntentKeys } from '@/lib/inventoryFlow/intentKey'
import { statusMeta } from '@/lib/inventoryFlow/strings'
import type { RequisitionStatus } from '@/lib/inventoryFlow/types'
import { FetchedTimeline } from './DocumentTimeline'
import { PT } from '@/lib/inventoryFlow/planStrings'
import { RT } from '@/lib/inventoryFlow/cateringStrings'
import type { PlanRevision } from '@/lib/inventoryFlow/cateringTypes'
import { initialSendBack, sendBackLines } from '@/lib/inventoryFlow/cateringLogic'
import { bookingCancelledOnPlan, kitchenHas, kitchenNote, noRecipeDishes } from '@/lib/inventoryFlow/planLogic'
import type { PlanAction, PlanDemandRow, PlanOrderType, PlanStatus, ProductionPlan } from '@/lib/inventoryFlow/planTypes'
import {
  addDish, addExtra, askLines, dishesFromItems, dishesMissingQty, fmtAmount, hasEquipmentOut, initialAsks, isEditable, localTomorrow, pickableExtras, planActions,
  planInputFrom, planSheetHtml, removeDish, setDishQty, setExtraQty, type DraftExtra,
  sheetMode, type DraftDish,
} from '@/lib/inventoryFlow/planLogic'
import { addDays, askCounts, firstRows, fmtEventTime, nextStep, planName, statusLine, stepsDone, type NextStep } from '@/lib/inventoryFlow/todayLogic'
import { statusText, TT } from '@/lib/inventoryFlow/todayStrings'
import { StepStrip } from './PlanSteps'
import { ItemAvatar } from './StatusChip'
import { WrapUpPanel, WrapUpSummary } from './WrapUpPanel'
import { EquipmentReturnPanel } from './EquipmentReturnPanel'
import { EquipmentClashNotice } from '@/components/catering/EquipmentClashNotice'
import { EQ } from '@/lib/inventoryFlow/equipmentStrings'
import { canSettleEquipment } from '@/lib/inventoryFlow/equipmentLogic'
import { QtyStepper } from './QtyStepper'
import { DishRecipeButton, PrepRecipeButton } from '@/components/recipe-card/RecipeCardHost'

type Notify = (kind: 'success' | 'error', message: string) => void
type View = { kind: 'list' } | { kind: 'edit'; plan: ProductionPlan | null } | { kind: 'detail'; id: string }

const PLAN_KEY = ['inventory-flow', 'plans'] as const

function errMessage(e: unknown): string {
  return friendlyError(e, { brief: true })
}

export function PlansTab({ notify, initialPlanId, initialWrapUp, onExit, onOpenRequest }: {
  notify: Notify
  initialPlanId?: string
  /** Batch 4: open the first plan's wrap-up straight away (Today's "Wrap up"). */
  initialWrapUp?: boolean
  /** Batch 4: where "back" goes from the first plan (Today), instead of the list. */
  onExit?: Exit
  /** Batch 4: "Check it in" opens the plan's store request under Requests. */
  onOpenRequest?: (reqId: string) => void
}) {
  const [view, setView] = useState<View>(initialPlanId ? { kind: 'detail', id: initialPlanId } : { kind: 'list' })
  if (view.kind === 'edit') {
    return <PlanEditor plan={view.plan} notify={notify} onDone={(p) => setView(p ? { kind: 'detail', id: p.id } : { kind: 'list' })} />
  }
  if (view.kind === 'detail') {
    const first = view.id === initialPlanId
    return (
      <PlanDetail key={view.id} id={view.id} notify={notify} initialWrapUp={first && initialWrapUp} onOpenRequest={onOpenRequest}
        back={first && onExit ? onExit : { label: PT.back, go: () => setView({ kind: 'list' }) }}
        onEdit={(p) => setView({ kind: 'edit', plan: p })} />
    )
  }
  return <PlanList onOpen={(id) => setView({ kind: 'detail', id })} onNew={() => setView({ kind: 'edit', plan: null })} />
}

// ── list ────────────────────────────────────────────────────────────────────

const STATUS_CLS: Record<PlanStatus, string> = {
  draft: 'bg-muted text-foreground',
  confirmed: 'bg-blue-100 text-blue-900 dark:bg-blue-900/40 dark:text-blue-100',
  requisitioned: 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100',
  in_production: 'bg-orange-100 text-orange-900 dark:bg-orange-900/40 dark:text-orange-100',
  done: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100',
  cancelled: 'bg-muted text-muted-foreground line-through',
}

function PlanChip({ status }: { status: PlanStatus }) {
  return <span className={cn('inline-flex rounded-full px-2.5 py-1 text-xs font-semibold', STATUS_CLS[status])}>{PT.status[status]}</span>
}

function PlanList({ onOpen, onNew }: { onOpen: (id: string) => void; onNew: () => void }) {
  const { data, isLoading, isError, refetch, isFetching } = useQuery({ queryKey: [...PLAN_KEY, 'list'], queryFn: () => apiClient.getProductionPlans({ limit: 100 }) })
  const plans = data?.data ?? []
  return (
    <div className="space-y-4">
      <Button onClick={onNew}><Plus className="mr-2 h-4 w-4" /> {PT.newPlan}</Button>
      {isLoading ? (
        <p className="text-muted-foreground">{TT.loading}</p>
      ) : isError ? (
        <div className="space-y-3 rounded-lg border p-4">
          <p className="text-muted-foreground">{TT.loadFailed}</p>
          <Button variant="outline" disabled={isFetching} onClick={() => void refetch()}>{TT.retry}</Button>
        </div>
      ) : plans.length === 0 ? (
        <p className="text-muted-foreground">{PT.noPlans}</p>
      ) : (
        <ul className="divide-y rounded-lg border bg-card">
          {plans.map((p) => (
            <li key={p.id}>
              <button type="button" onClick={() => onOpen(p.id)} className="flex w-full flex-wrap items-center gap-3 p-4 text-left hover:bg-muted/50 touch-manipulation">
                <div className="min-w-[8rem] text-base font-semibold">{formatDateHuman(p.for_date)}</div>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{planName(p)}</div>
                  <div className="text-sm text-muted-foreground">{PT.dishCount(p.dish_count)} · {PT.portions(p.portion_count)}</div>
                </div>
                <PlanChip status={p.status} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// ── editor ──────────────────────────────────────────────────────────────────

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = window.setTimeout(() => setV(value), ms)
    return () => window.clearTimeout(t)
  }, [value, ms])
  return v
}

function PlanEditor({ plan, notify, onDone }: { plan: ProductionPlan | null; notify: Notify; onDone: (p: ProductionPlan | null) => void }) {
  const qc = useQueryClient()
  const [forDate, setForDate] = useState(plan?.for_date ?? localTomorrow())
  const [title, setTitle] = useState(plan?.title ?? '')
  const [notes, setNotes] = useState(plan?.notes ?? '')
  const [orderType, setOrderType] = useState<PlanOrderType>(plan?.order_type ?? 'dine_in')
  const [dishes, setDishes] = useState<DraftDish[]>(() => dishesFromItems(plan?.items))
  // Stage 2B: packaging & equipment added by hand.
  const [extras, setExtras] = useState<DraftExtra[]>(() => (plan?.extras ?? []).map((x) => ({ ...x })))
  const [search, setSearch] = useState('')
  const [saveKey] = useState(newIdempotencyKey)
  const debouncedSearch = useDebounced(search.trim(), 250)

  const { data: productsRes } = useQuery({
    queryKey: ['plan-dishes', debouncedSearch],
    queryFn: () => apiClient.getProducts({ search: debouncedSearch || undefined, per_page: 40, available: true }),
  })
  const products = productsRes?.data ?? []

  const input = useMemo(() => planInputFrom({ for_date: forDate, title, notes, order_type: orderType, dishes, extras }), [forDate, title, notes, orderType, dishes, extras])
  const previewInput = useDebounced(input, 400)
  const { data: previewRes, isFetching } = useQuery({
    queryKey: ['inventory-flow', 'plan-preview', previewInput.for_date, previewInput.order_type, previewInput.items, previewInput.extras],
    queryFn: () => apiClient.previewProductionPlan(previewInput),
    enabled: previewInput.items.length > 0 || (previewInput.extras ?? []).length > 0,
    retry: false,
  })
  const preview = previewInput.items.length > 0 || (previewInput.extras ?? []).length > 0 ? previewRes?.data : undefined
  const noRecipe = new Set(noRecipeDishes(dishes.map((d) => ({ product_id: d.product_id, name: d.name })), preview?.warnings).map((d) => d.product_id))

  const save = useMutation({
    mutationFn: () => apiClient.saveProductionPlan(input, plan?.id ?? null, saveKey),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['inventory-flow'] })
      notify('success', PT.saved)
      onDone(res.data ?? null)
    },
    onError: (e) => notify('error', errMessage(e)),
  })

  return (
    <div className="space-y-5">
      <Button variant="ghost" className="px-2" onClick={() => onDone(plan)}><ArrowLeft className="mr-2 h-4 w-4" /> {PT.back}</Button>
      <div className="grid gap-4 md:grid-cols-3">
        <label className="space-y-1.5">
          <span className="text-sm font-medium">{PT.forDate}</span>
          <Input type="date" value={forDate} onChange={(e) => setForDate(e.target.value)} />
        </label>
        <label className="space-y-1.5 md:col-span-2">
          <span className="text-sm font-medium">{PT.title}</span>
          <Input maxLength={120} placeholder={PT.titlePlaceholder} value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>
      </div>
      <div className="space-y-1.5">
        <span className="text-sm font-medium">{PT.served}</span>
        <div className="flex flex-wrap gap-2">
          {(['dine_in', 'takeaway'] as const).map((t) => (
            <button key={t} type="button" aria-pressed={orderType === t} onClick={() => setOrderType(t)}
              className={cn('h-10 rounded-lg border-2 px-4 text-sm font-semibold touch-manipulation',
                orderType === t ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card hover:bg-muted')}>
              {t === 'dine_in' ? PT.dineIn : PT.takeaway}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <section className="space-y-3">
          <h3 className="text-base font-semibold">{PT.dishes}</h3>
          <Input placeholder={PT.searchDishes} value={search} onChange={(e) => setSearch(e.target.value)} />
          <p className="text-sm text-muted-foreground">{PT.addDishHint}</p>
          <div className="grid max-h-[26rem] grid-cols-2 gap-2 overflow-y-auto sm:grid-cols-3">
            {products.length === 0 && <p className="col-span-full text-muted-foreground">{PT.noDishes}</p>}
            {products.map((p) => (
              <button key={p.id} type="button" onClick={() => setDishes((l) => addDish(l, { product_id: p.id, name: p.name, image_url: p.image_url }))}
                className="flex min-h-[4.5rem] items-center gap-2 rounded-lg border bg-card p-2 text-left hover:bg-muted touch-manipulation">
                {p.image_url ? <img src={p.image_url} alt="" className="h-10 w-10 shrink-0 rounded-lg object-cover" /> : <ItemAvatar name={p.name} />}
                <span className="line-clamp-2 text-sm font-medium">{p.name}</span>
              </button>
            ))}
          </div>
        </section>
        <section className="space-y-3">
          <h3 className="text-base font-semibold">{PT.dishCount(dishes.length)} · {PT.portions(dishes.reduce((s, d) => s + d.qty, 0))}</h3>
          <ul className="space-y-2">
            {dishes.map((d) => (
              <li key={d.product_id} className="flex flex-wrap items-center gap-3 rounded-lg border p-2">
                {d.image_url ? <img src={d.image_url} alt="" className="h-10 w-10 rounded-lg object-cover" /> : <ItemAvatar name={d.name} />}
                <span className="min-w-[8rem] flex-1 font-medium">{d.name}{noRecipe.has(d.product_id) && <NoRecipeTag />}</span>
                <QtyStepper value={d.qty} unit="" label={d.name} min={0} integer onChange={(v) => setDishes((l) => setDishQty(l, d.product_id, v))} />
                <Button variant="ghost" className="h-10 w-10 p-0" aria-label={PT.remove} onClick={() => setDishes((l) => removeDish(l, d.product_id))}>
                  <Trash2 className="h-4 w-4" />
                </Button>
                {!(d.qty > 0) && <p className="w-full text-sm text-destructive">{PT.enterHowMany}</p>}
              </li>
            ))}
          </ul>
          <ExtrasPicker extras={extras} setExtras={setExtras} />
          <label className="block space-y-1.5">
            <span className="text-sm font-medium">{PT.notes}</span>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </label>
          {dishesMissingQty(dishes) && <p className="text-sm text-destructive">{PT.dishNeedsQty}</p>}
          <Button className="w-full" disabled={(dishes.length === 0 && extras.length === 0) || !forDate || dishesMissingQty(dishes) || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? PT.saving : PT.saveDraft}
          </Button>
        </section>
      </div>

      <section className="space-y-2 rounded-lg border p-4">
        <h3 className="text-base font-semibold">{PT.sheet}{isFetching && <span className="ml-2 text-sm font-normal text-muted-foreground">…</span>}</h3>
        {!preview ? (
          <p className="text-muted-foreground">{PT.nothingYet}</p>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">{PT.sheetLive}</p>
            <Warnings warnings={preview.warnings} />
            <SheetTable demand={preview.demand} mode={{ ask: 'suggested', showStore: true }} />
            {!!preview.estimated_cost && <p className="text-sm font-medium">{PT.estimatedCost(formatMoney(preview.estimated_cost))}</p>}
            <MakeList produce={preview.produce} />
          </>
        )}
      </section>
    </div>
  )
}

// ── detail ──────────────────────────────────────────────────────────────────

type Exit = { label: string; go: () => void }

function PlanDetail({ id, notify, back, onEdit, initialWrapUp, onOpenRequest }: {
  id: string
  notify: Notify
  back: Exit
  onEdit: (p: ProductionPlan) => void
  /** Batch 4: opened from Today's "Wrap up" — open the wrap-up straight away. */
  initialWrapUp?: boolean
  onOpenRequest?: (reqId: string) => void
}) {
  const qc = useQueryClient()
  const role = getStoredUserRole()
  const { data, isLoading, error, refetch } = useQuery({ queryKey: [...PLAN_KEY, id], queryFn: () => apiClient.getProductionPlan(id) })
  const plan = data?.data
  const [asks, setAsks] = useState<Record<string, number>>({})
  const [cancelling, setCancelling] = useState(false)
  const [reason, setReason] = useState('')
  // Stage 2B: change the plan after it went to the store, then send back.
  const [revising, setRevising] = useState(false)
  const [lastRevision, setLastRevision] = useState<PlanRevision | null>(null)
  const [sendingBack, setSendingBack] = useState<Record<string, number> | null>(null)
  const [wrappingUp, setWrappingUp] = useState(false)
  // Batch 5: Send equipment back, with lost or broken.
  const [returningEquipment, setReturningEquipment] = useState(false)
  // Batch 2: "Lock the plan" asks first when a dish has no recipe.
  const [lockCheck, setLockCheck] = useState<string | null>(null)
  useEffect(() => {
    if (plan) setAsks(initialAsks(plan.demand ?? []))
  }, [plan?.id, plan?.updated_at]) // eslint-disable-line react-hooks/exhaustive-deps
  // Batch 4: from Today's "Wrap up", once, when the plan can be wrapped up as it is.
  const autoWrap = useRef(!!initialWrapUp)
  useEffect(() => {
    if (!autoWrap.current || !plan) return
    autoWrap.current = false
    if ((plan.status === 'in_production' || plan.status === 'done') && !plan.wrap_up) setWrappingUp(true)
  }, [plan])

  // One key per plan action on this version of the plan, reused on a retry.
  const keys = useIntentKeys()
  const actScope = (action: PlanAction) => intentScope(action, id, plan?.updated_at ?? '')
  const act = useMutation({
    mutationFn: ({ action, body }: { action: PlanAction; body?: unknown }) => apiClient.productionPlanAction(id, action, body, keys.keyFor(actScope(action))),
    onSuccess: (res, { action }) => {
      keys.renew(actScope(action))
      qc.invalidateQueries({ queryKey: ['inventory-flow'] })
      setCancelling(false)
      setLockCheck(null)
      notify('success', action === 'send-to-store' && !res.data?.requisition_id ? PT.sentNoRequest : PT.done[action])
    },
    onError: (e) => {
      qc.invalidateQueries({ queryKey: ['inventory-flow'] })
      notify('error', errMessage(e))
    },
  })

  if (isLoading) return <p className="text-muted-foreground">{TT.loading}</p>
  if (!plan) {
    return (
      <div className="space-y-3">
        <Button variant="ghost" className="px-2" onClick={back.go}><ArrowLeft className="mr-2 h-4 w-4" /> {back.label}</Button>
        <p className="text-destructive">{error ? errMessage(error) : ''}</p>
        <Button variant="outline" onClick={() => void refetch()}>{TT.retry}</Button>
      </div>
    )
  }

  if (revising) {
    return (
      <PlanReviser plan={plan} notify={notify} onCancel={() => setRevising(false)}
        onDone={(rev) => {
          setRevising(false)
          setLastRevision(rev)
          if (rev.decreases.length > 0) setSendingBack(initialSendBack(rev.decreases))
        }} />
    )
  }

  const actions = planActions(plan)
  const sent = Boolean(plan.sent_at)
  const canRevise = plan.status === 'requisitioned' || plan.status === 'in_production'
  const canSendBack = sent && plan.status !== 'cancelled' && !plan.wrap_up && (plan.demand ?? []).some((d) => !d.is_prep)
  const canWrap = (plan.status === 'in_production' || plan.status === 'done') && !plan.wrap_up
  const lostShown = (plan.returnables ?? []).some((r) => (r.lost_or_broken ?? 0) > 1e-6)
  const mode = sheetMode(plan.status, sent)
  const canAsk = mode.ask === 'edit'
  const missingRecipes = noRecipeDishes((plan.items ?? []).map((i) => ({ product_id: i.product_id, name: i.product_name })), plan.warnings)
  const noRecipe = new Set(missingRecipes.map((d) => d.product_id))
  const run = (action: PlanAction) => {
    if (action === 'cancel') return setCancelling(true)
    if (action === 'confirm' && missingRecipes.length > 0 && lockCheck === null) return setLockCheck(missingRecipes.map((d) => d.name).join(', '))
    if (action === 'wrap-up') {
      // Cooking or all cooked: open the wrap-up. Sent but never started: mark
      // it done first (the wrap-up needs cooking started or finished).
      if (plan.status === 'requisitioned') return act.mutate({ action: 'done' }, { onSuccess: () => setWrappingUp(true) })
      return setWrappingUp(true)
    }
    act.mutate({ action, body: action === 'send-to-store' ? { lines: askLines(plan.demand ?? [], asks) } : undefined })
  }
  const print = () => {
    const w = window.open('', '_blank')
    if (!w) return
    w.document.write(planSheetHtml(plan, { forDate: PT.forDate, dishes: PT.dishes, sheet: PT.sheet, colItem: PT.colItem, colNeeded: TT.colNeed, colAsk: TT.colAsk, makeTitle: PT.makeTitle, colMake: PT.colMake,
      customerNote: PT.customerNote, planNotes: PT.planNotes, bookingCancelled: PT.bookingCancelled }))
    w.document.close()
    w.focus()
    w.print()
  }

  // Batch 4: the step strip and the one next step.
  const stepIn = { status: plan.status, sent_at: plan.sent_at, wrapped: !!plan.wrap_up, requisition_id: plan.requisition_id, requisition_status: plan.requisition_status, for_date: plan.for_date }
  const today = addDays(localTomorrow(), -1)
  const step = nextStep(stepIn, today)
  const counts = askCounts(plan.demand ?? [], canAsk ? asks : Object.fromEntries((plan.demand ?? []).map((d) => [d.stock_item_id, d.suggested_request_qty])))
  const changeDishes = isEditable(plan.status) ? () => onEdit(plan) : plan.status === 'confirmed' ? () => act.mutate({ action: 'reopen' }) : canRevise ? () => setRevising(true) : null
  const next = nextBox(step, { counts, cooking: plan.status === 'in_production', late: plan.status === 'done' || plan.for_date < today, waitLine: statusText(statusLine(stepIn)) })
  const primary: { label: string; go: () => void } | null =
    step === 'confirm' ? { label: PT.actions.confirm, go: () => run('confirm') }
      : step === 'send-to-store' ? { label: PT.actions['send-to-store'], go: () => run('send-to-store') }
        : step === 'check-in' && plan.requisition_id && onOpenRequest ? { label: TT.checkIn, go: () => onOpenRequest(plan.requisition_id!) }
          : step === 'start' ? { label: PT.actions.start, go: () => run('start') }
            : step === 'wrap-up' && actions.includes('wrap-up') ? { label: PT.actions['wrap-up'], go: () => run('wrap-up') }
              : null
  // A quiet second button only where it saves a trip to More.
  const quiet: { label: string; go: () => void } | null =
    (step === 'wait' || step === 'check-in') && actions.includes('start') ? { label: TT.startAnyway, go: () => run('start') } : null
  // Everything else the plan can do, under More.
  const more: { key: string; label: string; go: () => void; danger?: boolean }[] = []
  if (changeDishes) more.push({ key: 'change', label: canRevise ? RT.changePlan : TT.changeDishes, go: changeDishes })
  if (actions.includes('plan-batches')) more.push({ key: 'plan-batches', label: PT.actions['plan-batches'], go: () => run('plan-batches') })
  if (actions.includes('start') && step !== 'start' && !quiet) more.push({ key: 'start', label: PT.actions.start, go: () => run('start') })
  if (actions.includes('send-to-store') && step !== 'send-to-store') more.push({ key: 'send', label: PT.actions['send-to-store'], go: () => run('send-to-store') })
  if (actions.includes('wrap-up') && !(step === 'wrap-up' && primary)) more.push({ key: 'wrap-up', label: PT.actions['wrap-up'], go: () => run('wrap-up') })
  if (canSendBack) more.push({ key: 'send-back', label: RT.sendBackTitle, go: () => setSendingBack((a) => a ?? {}) })
  if (actions.includes('cancel')) more.push({ key: 'cancel', label: PT.actions.cancel, go: () => run('cancel'), danger: true })

  const facts = [
    formatDateHuman(plan.for_date),
    fmtEventTime(plan.booking?.event_time),
    plan.booking?.guest_count ? TT.guests(plan.booking.guest_count) : '',
    plan.order_type === 'dine_in' ? TT.served : TT.packed,
    plan.title && plan.booking ? plan.booking.customer_name : '',
    plan.booking ? TT.booking(plan.booking.booking_no) : '',
    plan.plan_no,
  ].filter(Boolean)

  return (
    <div className="space-y-5">
      <Button variant="ghost" className="px-2" onClick={back.go}><ArrowLeft className="mr-2 h-4 w-4" /> {back.label}</Button>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-xl font-bold">{planName(plan)}</h2>
          <p className="text-sm text-muted-foreground">{facts.join(' · ')}</p>
          {plan.requisition_no && (
            <p className="mt-1 text-sm font-medium">
              {PT.requestLink(plan.requisition_no, plan.requisition_status ? statusMeta(plan.requisition_status as RequisitionStatus).label : '')}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <PlanChip status={plan.status} />
          <Button variant="outline" onClick={print}><Printer className="mr-2 h-4 w-4" /> {PT.print}</Button>
          {more.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" disabled={act.isPending}>{TT.more} <ChevronDown className="ml-1 h-4 w-4" /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {more.map((m) => (
                  <DropdownMenuItem key={m.key} className={cn(m.danger && 'text-destructive focus:text-destructive')} onSelect={m.go}>{m.label}</DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {bookingCancelledOnPlan(plan) && (
        <div role="alert" className="rounded-lg border-2 border-destructive bg-destructive/10 p-3 text-sm">
          <p className="font-bold text-destructive">{PT.bookingCancelled}</p>
          <p>{PT.bookingCancelledHelp}</p>
        </div>
      )}

      {plan.status !== 'cancelled' && <StepStrip done={stepsDone(stepIn)} />}

      {lockCheck !== null ? (
        <div className="space-y-3 rounded-lg border border-amber-400 bg-amber-50 p-4 text-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
          <p className="flex items-center gap-2 font-medium"><AlertTriangle className="h-4 w-4 shrink-0" /> {PT.lockNoRecipe(lockCheck)}</p>
          <div className="flex flex-wrap gap-2">
            <Button disabled={act.isPending} onClick={() => act.mutate({ action: 'confirm' })}>{PT.lockAnyway}</Button>
            <Button variant="ghost" onClick={() => setLockCheck(null)}>{PT.goBack}</Button>
          </div>
        </div>
      ) : next && !(canWrap && wrappingUp) ? (
        <section className="flex flex-wrap items-center gap-3 rounded-lg border-2 border-primary/30 bg-muted/40 p-4">
          <div className="min-w-0 flex-[1_1_18rem]">
            <p className="text-base font-semibold">{next.title}</p>
            <p className="text-sm text-muted-foreground">{next.help}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {quiet && <Button variant="outline" disabled={act.isPending} onClick={quiet.go}>{quiet.label}</Button>}
            {primary && <Button disabled={act.isPending} onClick={primary.go}>{primary.label}</Button>}
          </div>
        </section>
      ) : null}

      {cancelling && (
        <div className="flex flex-wrap gap-2 rounded-lg border-2 border-destructive/50 p-4">
          <Input className="min-w-[14rem] flex-1" placeholder={PT.cancelReason} value={reason} onChange={(e) => setReason(e.target.value)} />
          <Button variant="destructive" disabled={act.isPending} onClick={() => act.mutate({ action: 'cancel', body: { reason } })}>{PT.cancelConfirm}</Button>
          <Button variant="ghost" onClick={() => setCancelling(false)}>{PT.keep}</Button>
        </div>
      )}

      {missingRecipes.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-400 bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
          <p className="flex min-w-0 flex-[1_1_18rem] items-start gap-2"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden /> {TT.noRecipeWarn(missingRecipes.map((d) => d.name))}</p>
          {role === 'admin' && (
            <Button asChild variant="outline" size="sm">
              <Link to="/admin/menu" search={{ tab: 'costing', item: missingRecipes[0].product_id }}>{TT.addRecipe}</Link>
            </Button>
          )}
        </div>
      )}
      {plan.booking?.notes && (
        <div className="rounded-lg border border-amber-400 bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
          <p className="flex items-center gap-1.5 font-semibold"><AlertTriangle className="h-4 w-4" aria-hidden /> {PT.customerNote}</p>
          <p className="whitespace-pre-wrap">{plan.booking.notes}</p>
        </div>
      )}

      {lastRevision && <RevisionResult rev={lastRevision} onClose={() => setLastRevision(null)} />}
      {plan.wrap_up && <WrapUpSummary w={plan.wrap_up} />}
      {canWrap && wrappingUp && <WrapUpPanel planId={plan.id} notify={notify} onClose={() => setWrappingUp(false)} />}
      {canSendBack && (
        <SendBackPanel planId={plan.id} demand={plan.demand ?? []} amounts={sendingBack} setAmounts={setSendingBack} notify={notify} />
      )}

      <section className="rounded-lg border p-4">
        <div className="mb-2 flex items-center justify-between gap-3">
          <h3 className="text-base font-semibold">{PT.dishCount(plan.dish_count)} · {PT.portions(plan.portion_count)}</h3>
          {changeDishes && <Button variant="ghost" size="sm" disabled={act.isPending} onClick={changeDishes}>{TT.change}</Button>}
        </div>
        <ul className="grid max-w-3xl gap-2 sm:grid-cols-2">
          {(plan.items ?? []).map((i) => (
            <li key={i.id} className="flex items-center gap-3 rounded-lg border p-2">
              {i.image_url ? <img src={i.image_url} alt="" className="h-10 w-10 rounded-lg object-cover" /> : <ItemAvatar name={i.product_name} />}
              <span className="min-w-0 flex-1">
                <span className="block font-medium">{i.product_name}{noRecipe.has(i.product_id) && <NoRecipeTag />}</span>
                {i.note && <span className="block text-sm font-medium text-amber-700 dark:text-amber-300">{i.note}</span>}
              </span>
              <DishRecipeButton productId={i.product_id} name={i.product_name} qty={i.qty} orderType={plan.order_type} options={i.options} />
              <span className="text-base font-semibold tabular-nums">× {i.qty}</span>
            </li>
          ))}
        </ul>
        {plan.notes && (
          <div className="mt-3 space-y-0.5">
            <p className="text-sm font-semibold">{PT.planNotes}</p>
            <p className="whitespace-pre-wrap text-sm text-muted-foreground">{plan.notes}</p>
          </div>
        )}
        {canRevise && <p className="mt-2 text-xs text-muted-foreground">{RT.changeHelp}</p>}
      </section>

      <section className="space-y-2 rounded-lg border p-4">
        <h3 className="text-base font-semibold">{PT.sheet}</h3>
        <p className="text-sm text-muted-foreground">{plan.snapshot ? PT.sheetLocked : PT.sheetLive}</p>
        <Warnings warnings={(plan.warnings ?? []).filter((w) => w.code !== 'product_has_no_recipe')} />
        {canAsk && <p className="text-sm font-medium">{PT.askHint}</p>}
        <SheetTable demand={plan.demand ?? []} mode={mode} asks={canAsk ? asks : undefined} onAsk={(itemId, v) => setAsks((a) => ({ ...a, [itemId]: v }))}
          lockedOn={plan.snapshot ? (plan.confirmed_at ? formatDateHuman(plan.confirmed_at) : '') : undefined} />
        {!!plan.estimated_cost && <p className="text-sm font-medium">{PT.estimatedCost(formatMoney(plan.estimated_cost))}</p>}
        <MakeList produce={plan.produce ?? []} />
      </section>

      <EquipmentClashNotice clashes={plan.equipment_clashes} />
      {(plan.extras ?? []).length > 0 && (
        <section className="rounded-lg border p-4">
          <h3 className="mb-2 text-base font-semibold">{PT.extrasTitle}</h3>
          <ul className="grid max-w-3xl gap-2 sm:grid-cols-2">
            {(plan.extras ?? []).map((x) => (
              <li key={x.stock_item_id} className="flex items-center justify-between gap-3 rounded-lg border p-2">
                <span className="font-medium">{x.name}{x.is_returnable && <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-xs">{PT.returnable}</span>}</span>
                <span className="tabular-nums font-semibold">{fmtAmount(x.qty)} {x.unit}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {(plan.returnables ?? []).length > 0 && (
        <section className="space-y-2 rounded-lg border-2 border-amber-300 p-4 dark:border-amber-800">
          <h3 className="text-base font-semibold">{PT.equipmentOut}</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-muted-foreground">
                <tr><th className="p-2">{PT.colItem}</th><th className="p-2 text-right">{PT.sentOut}</th><th className="p-2 text-right">{PT.cameBack}</th>{lostShown && <th className="p-2 text-right">{EQ.lostColumn}</th>}<th className="p-2 text-right">{PT.stillOut}</th></tr>
              </thead>
              <tbody className="divide-y">
                {(plan.returnables ?? []).map((r) => (
                  <tr key={r.stock_item_id}>
                    <td className="p-2 font-medium">{r.name}</td>
                    <td className="p-2 text-right tabular-nums">{fmtAmount(r.sent)}</td>
                    <td className="p-2 text-right tabular-nums">{fmtAmount(r.returned)}</td>
                    {lostShown && <td className="p-2 text-right tabular-nums">{fmtAmount(r.lost_or_broken ?? 0)}</td>}
                    <td className={cn('p-2 text-right font-semibold tabular-nums', r.outstanding > 1e-6 && 'text-destructive')}>{fmtAmount(r.outstanding)} {r.unit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {returningEquipment ? (
            <EquipmentReturnPanel planId={plan.id} returnables={plan.returnables ?? []} notify={notify} onClose={() => setReturningEquipment(false)} />
          ) : canSettleEquipment(plan.returnables) ? (
            <Button variant="outline" disabled={act.isPending} onClick={() => setReturningEquipment(true)}>{PT.sendBack}</Button>
          ) : hasEquipmentOut(plan.returnables) ? (
            <p className="text-sm text-muted-foreground">{EQ.allOnWayBack}</p>
          ) : (
            <p className="text-sm text-muted-foreground">{PT.equipmentAllBack}</p>
          )}
        </section>
      )}

      {(plan.batches ?? []).length > 0 && (
        <section className="rounded-lg border p-4">
          <h3 className="mb-2 text-base font-semibold">{PT.batchesTitle}</h3>
          <ul className="divide-y">
            {(plan.batches ?? []).map((b) => (
              <li key={b.id} className="flex items-center justify-between gap-3 py-2">
                <span><span className="font-medium">{b.prep_name}</span> <span className="text-sm text-muted-foreground">{b.batch_no}</span></span>
                <span className="tabular-nums">{fmtAmount(b.planned_qty)} {b.unit} · {b.status.replace('_', ' ')}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Stage 3: everything that happened to this plan, with who did it — folded (Batch 4). */}
      <PlanHistory id={plan.id} />
    </div>
  )
}

/** Batch 4: the "Next: …" box's words for each next step. */
function nextBox(step: NextStep | null, c: { counts: { ask: number; have: number }; cooking: boolean; late: boolean; waitLine: string }): { title: string; help: string } | null {
  switch (step) {
    case 'confirm':
      return { title: TT.nextConfirm, help: TT.nextConfirmHelp }
    case 'send-to-store':
      return { title: TT.nextSend(c.counts.ask), help: TT.nextSendHelp(c.counts.have, c.cooking) }
    case 'check-in':
      return { title: TT.nextCheckIn, help: TT.nextCheckInHelp }
    case 'wait':
      return { title: TT.nextWait, help: TT.nextWaitHelp(c.waitLine) }
    case 'start':
      return { title: TT.nextStart, help: TT.nextStartHelp }
    case 'wrap-up':
      return { title: c.late ? TT.nextWrapLate : TT.nextWrap, help: TT.nextWrapHelp }
    default:
      return null
  }
}

/** Batch 4: the plan's timeline under a "History (N)" disclosure. Same query
 *  as the timeline itself, so opening it costs nothing more. */
function PlanHistory({ id }: { id: string }) {
  const [open, setOpen] = useState(false)
  const q = useQuery({ queryKey: ['inventory-flow', 'timeline', 'plan', id], queryFn: () => apiClient.getProductionPlanTimeline(id) })
  const n = q.data?.data?.events?.length ?? 0
  if (!q.isError && n === 0) return null
  return (
    <section className="rounded-lg border">
      <button type="button" aria-expanded={open} onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-3 p-4 text-left text-sm font-semibold hover:bg-muted/50">
        {TT.history(n)}
        <ChevronDown className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />
      </button>
      {open && <div className="border-t p-4"><FetchedTimeline kind="plan" id={id} /></div>}
    </section>
  )
}

// ── shared pieces ───────────────────────────────────────────────────────────

function NoRecipeTag() {
  return <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">{PT.noRecipe}</span>
}

function Warnings({ warnings }: { warnings: { code: string; message: string }[] }) {
  if (warnings.length === 0) return null
  return (
    <div className="rounded-lg border border-amber-400 bg-amber-50 p-3 text-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
      <p className="flex items-center gap-2 font-semibold"><AlertTriangle className="h-4 w-4" /> {PT.warningsTitle}</p>
      <ul className="mt-1 list-disc pl-5 text-sm">
        {warnings.map((w, i) => <li key={`${w.code}-${i}`}>{w.message}</li>)}
      </ul>
    </div>
  )
}

function SheetTable({ demand, mode, asks, onAsk, lockedOn }: {
  demand: PlanDemandRow[]
  mode: { ask: 'edit' | 'asked' | 'suggested'; showStore: boolean }
  asks?: Record<string, number>
  onAsk?: (id: string, v: number) => void
  /** Set on a locked plan: when its kitchen figures were counted ('' if unknown). */
  lockedOn?: string
}) {
  const [showAll, setShowAll] = useState(false)
  // Batch 4: rows the kitchen covers read "Have it"; "Ask anyway" opens the amount.
  const [opened, setOpened] = useState<Set<string>>(() => new Set())
  const raw = demand.filter((d) => !d.is_prep)
  if (raw.length === 0) return null
  const { shown, hidden } = firstRows(raw, showAll)
  const showCost = raw.some((d) => !!d.unit_cost)
  const haveIt = <span className="font-medium text-emerald-700 dark:text-emerald-300">{TT.haveIt}</span>
  return (
    <div className="space-y-2">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-muted-foreground">
            <tr>
              <th className="p-2 font-medium">{PT.colItem}</th>
              <th className="whitespace-nowrap p-2 text-right font-medium">{TT.colNeed}</th>
              <th className="whitespace-nowrap p-2 text-right font-medium">{TT.colFree}</th>
              {mode.showStore && <th className="whitespace-nowrap p-2 text-right font-medium">{TT.colStore}</th>}
              <th className="whitespace-nowrap p-2 text-right font-medium">{mode.ask === 'asked' ? TT.colAsked : TT.colAsk}</th>
              {showCost && <th className="whitespace-nowrap p-2 text-right font-medium">{TT.colCost}</th>}
            </tr>
          </thead>
          <tbody className="divide-y">
            {shown.map((d) => {
              const k = kitchenHas(d, lockedOn !== undefined)
              const n = kitchenNote(d)
              const amount = mode.ask === 'asked' ? d.requested_qty ?? 0 : d.suggested_request_qty
              const editing = !!asks && !!onAsk
              const current = editing ? asks[d.stock_item_id] ?? 0 : amount
              return (
                <tr key={d.stock_item_id}>
                  <td className="p-2">
                    <div className="font-medium">{d.name}</div>
                    {d.via.length > 0 && <div className="text-xs text-muted-foreground">{PT.viaPrep(d.via.map((v) => v.prep_name).join(', '))}</div>}
                  </td>
                  <td className="p-2 text-right tabular-nums">{fmtAmount(d.net_qty)} {d.unit}</td>
                  <td className="p-2 text-right tabular-nums text-muted-foreground">
                    {fmtAmount(k.main)} {d.unit}
                    {k.counted != null && <div className="text-xs">{PT.countedOn(fmtAmount(k.counted), lockedOn ?? '')}</div>}
                    {n && <div className="text-xs text-amber-700 dark:text-amber-300">{PT.kitchenBreakdown(fmtAmount(n.total), fmtAmount(n.reserved), fmtAmount(n.expired))}</div>}
                  </td>
                  {mode.showStore && (
                    <td className={cn('p-2 text-right tabular-nums', d.store_on_hand + 1e-9 < d.suggested_request_qty ? 'font-semibold text-destructive' : 'text-muted-foreground')}>
                      {fmtAmount(d.store_on_hand)} {d.unit}
                    </td>
                  )}
                  <td className="p-2 text-right">
                    {editing && (d.suggested_request_qty > 1e-6 || current > 1e-6 || opened.has(d.stock_item_id)) ? (
                      <QtyStepper className="justify-end" value={current} unit={d.unit} label={d.name} min={0}
                        onChange={(v) => onAsk!(d.stock_item_id, v)} />
                    ) : editing ? (
                      <span className="inline-flex flex-wrap items-center justify-end gap-2">
                        {haveIt}
                        <Button variant="ghost" size="sm" onClick={() => setOpened((s) => new Set(s).add(d.stock_item_id))}>{TT.askAnyway}</Button>
                      </span>
                    ) : amount > 1e-6 ? (
                      <span className="font-semibold tabular-nums">{fmtAmount(amount)} {d.unit}</span>
                    ) : haveIt}
                  </td>
                  {showCost && <td className="p-2 text-right tabular-nums text-muted-foreground">{d.unit_cost ? formatMoney(d.required_qty * d.unit_cost) : '—'}</td>}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      {(hidden > 0 || showAll) && raw.length > 9 && (
        <Button variant="ghost" size="sm" onClick={() => setShowAll((s) => !s)}>{showAll ? TT.showFewer : TT.showAll(raw.length)}</Button>
      )}
    </div>
  )
}

function MakeList({ produce }: { produce: { prep_item_id: string; name: string; unit: string; produce_qty: number; runs: number; kitchen_on_hand: number }[] }) {
  const make = produce.filter((p) => p.produce_qty > 1e-6)
  return (
    <div className="pt-2">
      <h4 className="font-semibold">{PT.makeTitle}</h4>
      {make.length === 0 ? (
        <p className="text-sm text-muted-foreground">{PT.makeNone}</p>
      ) : (
        <ul className="divide-y">
          {make.map((p) => (
            <li key={p.prep_item_id} className="flex items-center justify-between gap-3 py-2">
              <span className="flex-1 font-medium">{p.name}</span>
              <PrepRecipeButton prepItemId={p.prep_item_id} name={p.name} qty={p.produce_qty} />
              <span className="tabular-nums">
                <strong>{fmtAmount(p.produce_qty)} {p.unit}</strong>
                <span className="ml-2 text-sm text-muted-foreground">{PT.runs(fmtAmount(p.runs))}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// ── Stage 2B: packaging & equipment picker ──────────────────────────────────

export function ExtrasPicker({ extras, setExtras }: { extras: DraftExtra[]; setExtras: (f: (l: DraftExtra[]) => DraftExtra[]) => void }) {
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(extras.length > 0)
  const { data } = useQuery({ queryKey: ['inventory-flow', 'items'], queryFn: () => apiClient.getFlowItems(), enabled: open, staleTime: 60_000 })
  const choices = pickableExtras(data?.data ?? [], search, 30)
  return (
    <section className="space-y-2 rounded-lg border p-3">
      <button type="button" className="flex w-full items-center justify-between text-left" onClick={() => setOpen((o) => !o)}>
        <span className="text-sm font-semibold">{PT.extrasTitle} {extras.length > 0 && `(${extras.length})`}</span>
        <Plus className={cn('h-4 w-4 transition-transform', open && 'rotate-45')} />
      </button>
      {open && (
        <>
          <p className="text-sm text-muted-foreground">{PT.extrasHint}</p>
          <ul className="space-y-2">
            {extras.map((x) => (
              <li key={x.stock_item_id} className="flex flex-wrap items-center gap-2">
                <span className="min-w-[8rem] flex-1 font-medium">{x.name}{x.is_returnable && <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-xs">{PT.returnable}</span>}</span>
                <QtyStepper value={x.qty} unit={x.unit} label={x.name} min={0} onChange={(v) => setExtras((l) => setExtraQty(l, x.stock_item_id, v))} />
              </li>
            ))}
          </ul>
          <Input placeholder={PT.searchItems} value={search} onChange={(e) => setSearch(e.target.value)} />
          <div className="flex max-h-48 flex-wrap gap-2 overflow-y-auto">
            {choices.length === 0 && <p className="text-sm text-muted-foreground">{PT.noItemsMatch}</p>}
            {choices.map((i) => (
              <button key={i.id} type="button"
                className="min-h-10 rounded-full border px-3 text-sm hover:bg-muted touch-manipulation"
                onClick={() => setExtras((l) => addExtra(l, { stock_item_id: i.id, name: i.name, unit: i.unit, is_returnable: Boolean(i.is_returnable) }))}>
                {i.name}
              </button>
            ))}
          </div>
        </>
      )}
    </section>
  )
}

// ── Stage 2B: change a plan after it was made; send back what is not needed ─

function PlanReviser({ plan, notify, onCancel, onDone }: {
  plan: ProductionPlan; notify: Notify; onCancel: () => void; onDone: (rev: PlanRevision) => void
}) {
  const qc = useQueryClient()
  const [dishes, setDishes] = useState<DraftDish[]>(() => dishesFromItems(plan.items))
  const [extras, setExtras] = useState<DraftExtra[]>(() => (plan.extras ?? []).map((x) => ({ ...x })))
  const [search, setSearch] = useState('')
  const [key] = useState(newIdempotencyKey)
  const debouncedSearch = useDebounced(search.trim(), 250)
  const { data: productsRes } = useQuery({
    queryKey: ['plan-dishes', debouncedSearch],
    queryFn: () => apiClient.getProducts({ search: debouncedSearch || undefined, per_page: 40, available: true }),
  })
  const products = productsRes?.data ?? []
  const input = planInputFrom({ for_date: plan.for_date, title: '', notes: '', order_type: plan.order_type, dishes, extras })
  const apply = useMutation({
    mutationFn: () => apiClient.reviseProductionPlan(plan.id, { items: input.items, extras: input.extras }, key),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['inventory-flow'] })
      const rev = res.data?.revision
      if (rev) {
        notify('success', rev.top_up_req_no ? RT.topUp(rev.top_up_req_no) : rev.mode === 'reopened' ? RT.reopened : RT.updated)
        onDone(rev)
      } else onCancel()
    },
    onError: (e) => notify('error', errMessage(e)),
  })
  return (
    <div className="space-y-5">
      <Button variant="ghost" className="px-2" onClick={onCancel}><ArrowLeft className="mr-2 h-4 w-4" /> {PT.back}</Button>
      <div>
        <h2 className="text-xl font-bold">{RT.changePlan} — {plan.title || plan.plan_no}</h2>
        <p className="text-muted-foreground">{RT.changeHelp}</p>
      </div>
      <div className="grid gap-5 lg:grid-cols-2">
        <section className="space-y-3">
          <Input placeholder={PT.searchDishes} value={search} onChange={(e) => setSearch(e.target.value)} />
          <div className="grid max-h-[22rem] grid-cols-2 gap-2 overflow-y-auto sm:grid-cols-3">
            {products.map((p) => (
              <button key={p.id} type="button" onClick={() => setDishes((l) => addDish(l, { product_id: p.id, name: p.name, image_url: p.image_url }))}
                className="flex min-h-[4.5rem] items-center gap-2 rounded-lg border bg-card p-2 text-left hover:bg-muted touch-manipulation">
                {p.image_url ? <img src={p.image_url} alt="" className="h-10 w-10 shrink-0 rounded-lg object-cover" /> : <ItemAvatar name={p.name} />}
                <span className="line-clamp-2 text-sm font-medium">{p.name}</span>
              </button>
            ))}
          </div>
        </section>
        <section className="space-y-3">
          <ul className="space-y-2">
            {dishes.map((d) => (
              <li key={d.product_id} className="flex flex-wrap items-center gap-3 rounded-lg border p-2">
                <span className="min-w-[8rem] flex-1 font-medium">{d.name}</span>
                <QtyStepper value={d.qty} unit="" label={d.name} min={0} integer onChange={(v) => setDishes((l) => setDishQty(l, d.product_id, v))} />
                <Button variant="ghost" className="h-10 w-10 p-0" aria-label={PT.remove} onClick={() => setDishes((l) => removeDish(l, d.product_id))}>
                  <Trash2 className="h-4 w-4" />
                </Button>
                {!(d.qty > 0) && <p className="w-full text-sm text-destructive">{PT.enterHowMany}</p>}
              </li>
            ))}
          </ul>
          <ExtrasPicker extras={extras} setExtras={setExtras} />
          {dishesMissingQty(dishes) && <p className="text-sm text-destructive">{PT.dishNeedsQty}</p>}
          <Button className="w-full" disabled={(dishes.length === 0 && extras.length === 0) || dishesMissingQty(dishes) || apply.isPending} onClick={() => apply.mutate()}>
            {apply.isPending ? RT.applying : RT.applyChange}
          </Button>
        </section>
      </div>
    </div>
  )
}

function RevisionResult({ rev, onClose }: { rev: PlanRevision; onClose: () => void }) {
  const list = (rows: PlanRevision['increases'], sign: string) => (
    <ul className="text-sm">
      {rows.map((r) => <li key={r.stock_item_id} className="flex justify-between gap-3"><span>{r.name}</span><span className="tabular-nums font-semibold">{sign}{fmtAmount(r.qty)} {r.unit}</span></li>)}
    </ul>
  )
  return (
    <section className="space-y-3 rounded-lg border-2 border-blue-300 p-4 dark:border-blue-800">
      {rev.top_up_req_no && <p className="font-semibold">{RT.topUp(rev.top_up_req_no)}</p>}
      {rev.mode === 'reopened' && <p className="font-semibold">{RT.reopened}</p>}
      {rev.increases.length === 0 && rev.decreases.length === 0 && rev.mode === 'revised' && <p>{RT.noDifference}</p>}
      <div className="grid gap-4 sm:grid-cols-2">
        {rev.increases.length > 0 && <div><h4 className="font-semibold">{RT.more}</h4>{list(rev.increases, '+')}</div>}
        {rev.decreases.length > 0 && <div><h4 className="font-semibold">{RT.less}</h4>{list(rev.decreases, '−')}</div>}
      </div>
      <Button variant="ghost" className="h-10" onClick={onClose}>OK</Button>
    </section>
  )
}

function SendBackPanel({ planId, demand, amounts, setAmounts, notify }: {
  planId: string
  demand: PlanDemandRow[]
  amounts: Record<string, number> | null
  setAmounts: (a: Record<string, number> | null) => void
  notify: Notify
}) {
  const qc = useQueryClient()
  const raw = demand.filter((d) => !d.is_prep)
  // One key per send-back as typed: the same amounts retried reuse it.
  const intent = useIntentKey(intentScope('return-stock', planId, sendBackLines(amounts ?? {})))
  const send = useMutation({
    mutationFn: () => apiClient.returnPlanStock(planId, sendBackLines(amounts ?? {}), undefined, intent.key),
    onSuccess: () => {
      intent.renew()
      qc.invalidateQueries({ queryKey: ['inventory-flow'] })
      setAmounts(null)
      notify('success', RT.sendBackDone)
    },
    onError: (e) => {
      qc.invalidateQueries({ queryKey: ['inventory-flow'] })
      notify('error', errMessage(e))
    },
  })
  // Batch 4: opened from the plan's More menu ("Send back to store").
  if (raw.length === 0 || !amounts) return null
  const lines = sendBackLines(amounts)
  return (
    <section className="space-y-3 rounded-lg border-2 border-amber-300 p-4 dark:border-amber-800">
      <h3 className="text-base font-semibold">{RT.sendBackTitle}</h3>
      <p className="text-sm text-muted-foreground">{RT.sendBackHelp}</p>
      <ul className="space-y-2">
        {raw.map((d) => (
          <li key={d.stock_item_id} className="flex flex-wrap items-center gap-3">
            <span className="min-w-[8rem] flex-1 font-medium">{d.name}<span className="ml-2 text-sm text-muted-foreground">{PT.colKitchen} {fmtAmount(d.kitchen_total ?? d.kitchen_on_hand)}</span></span>
            <QtyStepper value={amounts[d.stock_item_id] ?? 0} unit={d.unit} label={d.name} min={0}
              onChange={(v) => setAmounts({ ...amounts, [d.stock_item_id]: v })} />
          </li>
        ))}
      </ul>
      {lines.length === 0 && <p className="text-sm text-muted-foreground">{RT.nothingToSend}</p>}
      <div className="flex gap-2">
        <Button disabled={lines.length === 0 || send.isPending} onClick={() => send.mutate()}>{RT.sendBack}</Button>
        <Button variant="ghost" onClick={() => setAmounts(null)}>{PT.keep}</Button>
      </div>
    </section>
  )
}
